'use client';

import Image from '@/components/Image';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { usePinkLeopardTracking } from '../hooks/usePinkLeopardTracking';

interface ProductShowcaseProps {
  showcaseData: {
    title_word?: string; // Vue版本使用的字段名
    description?: string;
    btn_word?: string; // 按钮文字
    aspectRatio?: string; // 宽高比
    lists: Array<{
      id: number;
      bg_img?: string; // 背景图片
      title?: string;
      shop_sku?: string;
      business_sku?: string;
      sku?: string;
      short_name?: string;
      name?: string;
      image?: { product?: string[] };
      price_info?: any;
      color_arr?: Array<{
        color?: string;
        color_bg_image?: string;
      }>;
      chooseIndex?: number;
    }>;
  };
  productList: any[];
  deviceType: EPlatform;
  sdName: string; // 埋点名称，如"商品位1"、"商品位2"
  containerStyle?: string; // WAP端商品项容器样式
  imageFilter?: string; // WAP端图片滤镜效果
}

export default function ProductShowcase({
  showcaseData,
  productList,
  deviceType,
  sdName,
  containerStyle = '',
  imageFilter = '',
}: ProductShowcaseProps) {
  const { trackProductPitsExposure, trackProductPitsClick, trackBuyNowClick, trackColorClick } =
    usePinkLeopardTracking();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const { navigate } = useAppNavigation();

  // 处理颜色选择的本地状态
  const [colorSelections, setColorSelections] = useState<{ [key: number]: number }>({});

  // 曝光状态管理 - 使用useRef避免重新渲染
  const exposedItems = useRef<Set<number>>(new Set());

  // 商品曝光组件 - 独立于状态变化，只触发一次
  const ProductItemWithExposure = React.memo(
    ({ product, index, children }: { product: any; index: number; children: React.ReactNode }) => {
      const { ref: exposureRef, inView: isExposureInView } = useInView({
        threshold: 0.8,
        triggerOnce: true,
        rootMargin: '0px 0px -10% 0px', // 性能优化：提前10%触发
      });

      useEffect(() => {
        if (isExposureInView && !exposedItems.current.has(index)) {
          // 延迟上报，减少滚动时的性能冲击
          const timer = setTimeout(() => {
            trackProductPitsExposure(product, index, sdName);
            exposedItems.current.add(index);
          }, 150);

          return () => clearTimeout(timer);
        }
      }, [isExposureInView, product, index]);

      return (
        <div ref={exposureRef} className="w-full">
          {children}
        </div>
      );
    },
  );

  ProductItemWithExposure.displayName = 'ProductItemWithExposure';

  // 获取商品价格信息 - 使用项目统一的priceInfo方法
  const getPriceInfo = useCallback(
    (item: any) => {
      return priceInfo(userInfo, item, currency);
    },
    [userInfo, currency],
  );

  // 获取选中的颜色图片 - 使用useCallback避免重新渲染
  const getChosenImage = useCallback(
    (product: any, productIndex: number) => {
      const chosenIndex = colorSelections[productIndex] || product.chooseIndex || 0;
      return product.color_arr?.[chosenIndex]?.color_bg_image || '';
    },
    [colorSelections],
  );

  // 使用useMemo缓存商品列表渲染，只有颜色选择变化时才重新渲染相关商品
  const memoizedProductList = useMemo(() => {
    return productList.map((product, index) => {
      const selectedColorIndex = colorSelections[index];
      return {
        ...product,
        selectedColorIndex,
        priceInfo: getPriceInfo(product),
        chosenImage: getChosenImage(product, index),
      };
    });
  }, [productList, colorSelections, getPriceInfo, getChosenImage]);

  // 处理商品跳转 - 使用useCallback避免重新渲染
  const handleProductJump = useCallback(
    (e: React.MouseEvent, product: any, index: number) => {
      e.preventDefault(); // 阻止a标签默认跳转
      trackProductPitsClick(product, index, sdName);

      // 跳转到商品详情页
      if (product.shop_sku) {
        navigate(`/goods-detail/${product.shop_sku}`);
      }
    },
    [trackProductPitsClick, sdName, navigate],
  );

  // 处理购买按钮点击 - 使用useCallback避免重新渲染
  const handleBuyNowClick = useCallback(
    (e: React.MouseEvent, product: any, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      trackBuyNowClick(product, index, sdName);
      if (product.shop_sku) {
        navigate(`/goods-detail/${product.shop_sku}`);
      }
    },
    [trackBuyNowClick, sdName],
  );

  // 处理颜色选择
  const handleColorChange = (
    e: React.MouseEvent,
    colorItem: any,
    colorIndex: number,
    productIndex: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // 如果颜色没有变化，不触发更新
    if (colorSelections[productIndex] === colorIndex) {
      return;
    }

    setColorSelections((prev) => ({
      ...prev,
      [productIndex]: colorIndex,
    }));

    // 触发埋点
    trackColorClick(colorItem, productIndex, sdName);
  };

  const isWeb = deviceType === EPlatform.WEB;
  const widthPercentage = productList.length > 0 ? (100 / productList.length).toFixed(1) : '100';

  if (!productList.length) return null;

  return (
    <div
      className={`w-full ${isWeb ? 'p-0' : 'px-[14px] py-[20px]'}`}
      data-section="product-showcase"
    >
      {/* WAP端：显示标题和描述 */}
      {!isWeb && (
        <>
          {showcaseData.title_word && (
            <h2 className="title text-[25px] font-bs text-center">{showcaseData.title_word}</h2>
          )}

          {showcaseData.description && (
            <h3
              className="description text-[12px] leading-[20px] text-center"
              dangerouslySetInnerHTML={{ __html: showcaseData.description }}
            />
          )}
        </>
      )}

      {/* PC端：水平flex布局 - 按照Vue PC端实现 */}
      <WebOnly>
        <div className="personality-goods-list">
          {/* PC端标题描述区域 */}
          {(showcaseData.title_word || showcaseData.description) && (
            <div
              className="word-box text-center
               md:py-[30px] md:px-[80px]
               lg:py-[40px] lg:px-[107px] 
               xl:py-[50px] xl:px-[134px]
               2xl:py-[55px] 2xl:px-[145px]
               3xl:py-[60px] 3xl:px-[160px]"
            >
              {showcaseData.title_word && (
                <h2
                  className="title font-bb
                   md:text-[25px] 
                   lg:text-[33px]
                   xl:text-[42px]
                   2xl:text-[46px]
                   3xl:text-[50px]"
                >
                  {showcaseData.title_word}
                </h2>
              )}

              {showcaseData.description && (
                <h3
                  className="description
                     md:mt-[10px] md:text-[10px] md:leading-[18px]
                     lg:mt-[13px] lg:text-[13px] lg:leading-[24px]
                     xl:mt-[17px] xl:text-[17px] xl:leading-[30px]
                     2xl:mt-[18px] 2xl:text-[18px] 2xl:leading-[32px]
                     3xl:mt-[20px] 3xl:text-[20px] 3xl:leading-[36px]"
                  dangerouslySetInnerHTML={{ __html: showcaseData.description }}
                />
              )}
            </div>
          )}

          {/* PC端商品水平展示 */}
          <div className="product-wrap flex w-full">
            {memoizedProductList.map((product, index) => (
              <ProductItemWithExposure
                key={`${product.shop_sku}-${index}`}
                product={product}
                index={index}
              >
                <a
                  href={`/goods-detail/${product.shop_sku}`}
                  className="product-item relative flex-1 cursor-pointer"
                  style={{ width: `${widthPercentage}%` }}
                  aria-label={product.title}
                  onClick={(e) => handleProductJump(e, product, index)}
                >
                  <Image
                    src={product.bg_img}
                    alt={product.title}
                    className="w-full h-auto block"
                    width={0}
                    height={0}
                    sizes="100vw"
                    style={{ width: '100%', height: 'auto' }}
                  />
                  {product.title && (
                    <p
                      className="absolute text-white font-bb
                         md:bottom-[26px] md:left-[26px] md:text-[36px] md:leading-[30px]
                         lg:bottom-[35px] lg:left-[35px] lg:text-[48px] lg:leading-[40px]
                         xl:bottom-[44px] xl:left-[44px] xl:text-[60px] xl:leading-[50px]
                         2xl:bottom-[48px] 2xl:left-[48px] 2xl:text-[66px] 2xl:leading-[55px]
                         3xl:bottom-[53px] 3xl:left-[53px] 3xl:text-[72px] 3xl:leading-[60px]"
                      dangerouslySetInnerHTML={{ __html: product.title }}
                    />
                  )}
                </a>
              </ProductItemWithExposure>
            ))}
          </div>
        </div>
      </WebOnly>

      {/* WAP端：垂直列表布局 - 完全按照Vue版本样式 */}
      <WapOnly>
        <ul className="list-part pt-[20px] px-0">
          {memoizedProductList.map((product, index) => (
            <ProductItemWithExposure
              key={`${product.shop_sku}-${index}`}
              product={product}
              index={index}
            >
              <li>
                <a
                  href={`/goods-detail/${product.shop_sku}`}
                  aria-label={product.short_name}
                  className="product-item block mb-[12px] rounded-[10px] overflow-hidden"
                  onClick={(e) => handleProductJump(e, product, index)}
                >
                  {/* 普通商品样式 - 完全按照Vue WAP端样式 */}
                  {!product.color_arr && (
                    <div
                      className={`item-wrap flex justify-between items-center w-full h-[180px] ${containerStyle}`}
                      style={{ aspectRatio: '347/180' }}
                    >
                      {/* 左侧图片区域 - 固定200px宽度 */}
                      <div className="img_wrap relative w-[200px] h-full">
                        <Image
                          src={product.bg_img}
                          alt={product.name}
                          className={`w-auto h-full object-cover ${imageFilter ? 'contrast-90' : ''}`}
                          width={200}
                          height={180}
                          sizes="200px"
                        />
                        {product.title && (
                          <p
                            className="absolute bottom-[10px] left-[10px] text-white text-[15px] font-bs"
                            dangerouslySetInnerHTML={{ __html: product.title }}
                          />
                        )}
                      </div>

                      {/* 右侧信息区域 - flex-1 */}
                      <div className="info-wrap flex-1 flex flex-col items-center justify-center">
                        {product.image?.product?.[0] && (
                          <Image
                            src={product.image.product[0]}
                            alt={product.name}
                            className={`mt-[7px] w-[100px] h-auto ${imageFilter ? 'contrast-90' : ''}`}
                            width={100}
                            height={0}
                            sizes="100px"
                            style={{ height: 'auto' }}
                          />
                        )}
                        <p className="m-[5px_0_8px] text-[12px]">{product.short_name}</p>

                        {/* 价格信息 - 按Vue样式调整 */}
                        <div className="right-part flex flex-col mb-[5px]">
                          <span className="now-price notranslate text-[12px] font-bb text-primary-black text-center">
                            {formatPriceNumberWithSymbol(product.priceInfo.sell_price, currency)}
                          </span>
                          {product.priceInfo.sell_price !== product.priceInfo.price && (
                            <span className="old-price notranslate text-[12px] text-auxiliary-black line-through text-center">
                              {formatPriceNumberWithSymbol(product.priceInfo.price, currency)}
                            </span>
                          )}
                        </div>

                        {/* 购买按钮 - 使用Vue样式 */}
                        {showcaseData.btn_word && (
                          <div
                            className="btn_word w-[85px] h-[23px] bg-primary-black rounded-[13px] text-[10px] text-white leading-[25px] text-center cursor-pointer"
                            onClick={(e) => handleBuyNowClick(e, product, index)}
                          >
                            {showcaseData.btn_word}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* 多色商品样式 - 完全按照Vue WAP端样式 */}
                  {product.color_arr && (
                    <div className={`item-wrap-color relative ${containerStyle}`}>
                      <Image
                        src={product.chosenImage}
                        alt={product.name}
                        className="block w-full h-auto"
                        width={347}
                        height={300}
                        style={{ width: '100%', height: 'auto' }}
                      />

                      <div className="position-wrap absolute bottom-0 left-0 w-full">
                        {/* 颜色选择器 - 按Vue样式调整 */}
                        <ul className="color-list flex justify-center items-center">
                          {product.color_arr.map((colorItem, colorIndex) => (
                            <li
                              key={colorIndex}
                              className={`w-[25px] h-[25px] rounded-full mx-[3px] box-border cursor-pointer ${
                                (colorSelections[index] || product.chooseIndex || 0) === colorIndex
                                  ? 'border border-[#979797]'
                                  : ''
                              }`}
                              onClick={(e) => handleColorChange(e, colorItem, colorIndex, index)}
                            >
                              <Image
                                src={colorItem.color}
                                alt=""
                                className="w-full h-full rounded-full"
                                width={25}
                                height={25}
                                sizes="25px"
                              />
                            </li>
                          ))}
                        </ul>

                        <p className="name m-[8px_0] text-[12px] text-white text-center">
                          {product.title}
                        </p>

                        <div className="info-wrap flex justify-between items-end pl-[20px] pr-[16px] pb-[18px]">
                          <div className="right-part flex flex-row items-center">
                            <span className="now-price text-[18px] font-bb text-white">
                              {formatPriceNumberWithSymbol(product.priceInfo.sell_price, currency)}
                            </span>
                            {product.priceInfo.sell_price !== product.priceInfo.price && (
                              <span className="old-price text-[12px] text-white line-through block ml-[2px]">
                                {formatPriceNumberWithSymbol(product.priceInfo.price, currency)}
                              </span>
                            )}
                          </div>

                          {/* 购买按钮 - 白色背景黑色文字 */}
                          {showcaseData.btn_word && (
                            <div
                              className="btn_word w-[85px] h-[26px] bg-white rounded-[20px] text-[10px] text-black leading-[28px] text-center cursor-pointer"
                              onClick={(e) => handleBuyNowClick(e, product, index)}
                            >
                              {showcaseData.btn_word}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </a>
              </li>
            </ProductItemWithExposure>
          ))}
        </ul>
      </WapOnly>
    </div>
  );
}
